import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import {
  BookOpen,
  Terminal,
  Lightbulb,
  FileText,
  AlertTriangle,
  CheckCircle,
  Info,
} from 'lucide-react';
import type { KeyInsight } from '@/types/lesson';

export type TabType = 'theory' | 'practice' | 'insights' | 'resources';

interface Tab {
  id: TabType;
  label: string;
  icon: React.ReactNode;
}

const tabs: Tab[] = [
  { id: 'theory', label: 'Теория', icon: <BookOpen className="w-4 h-4" /> },
  { id: 'practice', label: 'Практика', icon: <Terminal className="w-4 h-4" /> },
  { id: 'insights', label: 'Ключевые моменты', icon: <Lightbulb className="w-4 h-4" /> },
  { id: 'resources', label: 'Ресурсы', icon: <FileText className="w-4 h-4" /> },
];

interface TabSectionProps {
  /** Контент для вкладки "Теория" */
  theoryContent: React.ReactNode;
  /** Контент для вкладки "Практика" */
  practiceContent?: React.ReactNode;
  /** Контент для вкладки "Ключевые моменты" */
  insightsContent?: React.ReactNode;
  /** Контент для вкладки "Ресурсы" */
  resourcesContent?: React.ReactNode;
  /** Активная вкладка по умолчанию */
  defaultTab?: TabType;
  /** Дополнительные CSS классы */
  className?: string;
  /** Callback при смене вкладки */
  onTabChange?: (tab: TabType) => void;
}

/**
 * Компонент для отображения контента урока с вкладками
 */
export function TabSection({
  theoryContent,
  practiceContent,
  insightsContent,
  resourcesContent,
  defaultTab = 'theory',
  className,
  onTabChange,
}: TabSectionProps) {
  const [activeTab, setActiveTab] = useState<TabType>(defaultTab);
  const [isAnimating, setIsAnimating] = useState(false);

  // Определяем доступные вкладки на основе переданного контента
  const availableTabs = tabs.filter((tab) => {
    switch (tab.id) {
      case 'theory':
        return !!theoryContent;
      case 'practice':
        return !!practiceContent;
      case 'insights':
        return !!insightsContent;
      case 'resources':
        return !!resourcesContent;
      default:
        return false;
    }
  });

  // Если активная вкладка недоступна, переключаемся на первую доступную
  useEffect(() => {
    if (!availableTabs.find((t) => t.id === activeTab)) {
      setActiveTab(availableTabs[0]?.id || 'theory');
    }
  }, [availableTabs, activeTab]);

  const handleTabChange = (tabId: TabType) => {
    if (tabId !== activeTab) {
      setIsAnimating(true);
      setActiveTab(tabId);
      onTabChange?.(tabId);

      // Сбрасываем состояние анимации после завершения
      setTimeout(() => setIsAnimating(false), 300);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'theory':
        return theoryContent;
      case 'practice':
        return practiceContent;
      case 'insights':
        return insightsContent;
      case 'resources':
        return resourcesContent;
      default:
        return theoryContent;
    }
  };

  return (
    <div className={cn('flex flex-col', className)}>
      {/* Табы */}
      <div className="flex border-b border-slate-700 mb-4">
        {availableTabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => handleTabChange(tab.id)}
            className={cn(
              'flex items-center gap-2 px-4 py-3 text-sm font-medium',
              'border-b-2 transition-colors duration-200',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900',
              activeTab === tab.id
                ? 'border-orange-500 text-orange-500'
                : 'border-transparent text-slate-400 hover:text-slate-200 hover:border-slate-600'
            )}
            aria-selected={activeTab === tab.id}
            role="tab"
          >
            {tab.icon}
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Контент вкладки */}
      <div
        className={cn('flex-1 min-h-0', isAnimating && 'animate-fade-in')}
        role="tabpanel"
        aria-labelledby={activeTab}
      >
        {renderTabContent()}
      </div>
    </div>
  );
}

/**
 * Компонент для отображения списка ресурсов
 */
interface ResourceItem {
  title: string;
  url: string;
  description?: string;
  type?: 'docs' | 'article' | 'video' | 'exercise';
}

interface ResourceListProps {
  resources: ResourceItem[];
  className?: string;
}

export function ResourceList({ resources, className }: ResourceListProps) {
  return (
    <div className={cn('space-y-3', className)}>
      {resources.map((resource, index) => (
        <a
          key={index}
          href={resource.url}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(
            'block p-4 rounded-lg border border-slate-700',
            'bg-slate-800/50 hover:bg-slate-800 hover:border-slate-600',
            'transition-colors duration-200',
            'group'
          )}
        >
          <div className="flex items-start gap-3">
            <div className="text-slate-400 group-hover:text-orange-500 transition-colors">
              {resource.type === 'docs' && <BookOpen className="w-5 h-5" />}
              {resource.type === 'article' && <FileText className="w-5 h-5" />}
              {resource.type === 'video' && <Terminal className="w-5 h-5" />}
              {resource.type === 'exercise' && <Lightbulb className="w-5 h-5" />}
              {!resource.type && <FileText className="w-5 h-5" />}
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-medium text-slate-200 group-hover:text-orange-500 transition-colors">
                {resource.title}
              </h4>
              {resource.description && (
                <p className="mt-1 text-xs text-slate-400">{resource.description}</p>
              )}
            </div>
          </div>
        </a>
      ))}
    </div>
  );
}

/**
 * Компонент для отображения ключевых моментов (старый формат для обратной совместимости)
 */
interface InsightItem {
  title: string;
  description: string;
  icon?: React.ReactNode;
}

interface InsightListProps {
  insights: InsightItem[];
  className?: string;
}

export function InsightList({ insights, className }: InsightListProps) {
  return (
    <div className={cn('space-y-4', className)}>
      {insights.map((insight, index) => (
        <div
          key={index}
          className={cn(
            'flex gap-4 p-4 rounded-lg',
            'bg-gradient-to-r from-orange-500/10 to-transparent',
            'border-l-4 border-orange-500'
          )}
        >
          <div className="flex-shrink-0 text-orange-500">
            {insight.icon || <Lightbulb className="w-5 h-5" />}
          </div>
          <div>
            <h4 className="text-sm font-medium text-slate-200">{insight.title}</h4>
            <p className="mt-1 text-sm text-slate-400">{insight.description}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Компонент для отображения расширенной структуры KeyInsight
 */
interface KeyInsightViewProps {
  insight: KeyInsight | string;
  className?: string;
}

export function KeyInsightView({ insight, className }: KeyInsightViewProps) {
  // Если insight - строка, используем старый формат отображения
  if (typeof insight === 'string') {
    return (
      <div className={cn('space-y-4', className)}>
        <div
          className={cn(
            'flex gap-4 p-4 rounded-lg',
            'bg-gradient-to-r from-orange-500/10 to-transparent',
            'border-l-4 border-orange-500'
          )}
        >
          <div className="flex-shrink-0 text-orange-500">
            <Lightbulb className="w-5 h-5" />
          </div>
          <div>
            <p className="text-sm text-slate-300">{insight}</p>
          </div>
        </div>
      </div>
    );
  }

  // Расширенный формат
  const { main, example, warning, tip, additional } = insight;

  return (
    <div className={cn('space-y-4', className)}>
      {/* Основной инсайт */}
      <div
        className={cn(
          'flex gap-4 p-4 rounded-lg',
          'bg-gradient-to-r from-orange-500/10 to-transparent',
          'border-l-4 border-orange-500'
        )}
      >
        <div className="flex-shrink-0 text-orange-500">
          <Lightbulb className="w-5 h-5" />
        </div>
        <div>
          <h4 className="text-sm font-medium text-orange-400 mb-1">Ключевой момент</h4>
          <p className="text-sm text-slate-300">{main}</p>
        </div>
      </div>

      {/* Практический пример */}
      {example && (
        <div
          className={cn(
            'flex gap-4 p-4 rounded-lg',
            'bg-gradient-to-r from-blue-500/10 to-transparent',
            'border-l-4 border-blue-500'
          )}
        >
          <div className="flex-shrink-0 text-blue-400">
            <Info className="w-5 h-5" />
          </div>
          <div>
            <h4 className="text-sm font-medium text-blue-400 mb-1">Практический пример</h4>
            <p className="text-sm text-slate-300">{example}</p>
          </div>
        </div>
      )}

      {/* Предостережение */}
      {warning && (
        <div
          className={cn(
            'flex gap-4 p-4 rounded-lg',
            'bg-gradient-to-r from-yellow-500/10 to-transparent',
            'border-l-4 border-yellow-500'
          )}
        >
          <div className="flex-shrink-0 text-yellow-400">
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div>
            <h4 className="text-sm font-medium text-yellow-400 mb-1">Внимание</h4>
            <p className="text-sm text-slate-300">{warning}</p>
          </div>
        </div>
      )}

      {/* Совет */}
      {tip && (
        <div
          className={cn(
            'flex gap-4 p-4 rounded-lg',
            'bg-gradient-to-r from-green-500/10 to-transparent',
            'border-l-4 border-green-500'
          )}
        >
          <div className="flex-shrink-0 text-green-400">
            <CheckCircle className="w-5 h-5" />
          </div>
          <div>
            <h4 className="text-sm font-medium text-green-400 mb-1">Совет</h4>
            <p className="text-sm text-slate-300">{tip}</p>
          </div>
        </div>
      )}

      {/* Дополнительные инсайты */}
      {additional && additional.length > 0 && (
        <div className="space-y-2">
          {additional.map((item, index) => (
            <div
              key={index}
              className="flex gap-3 p-3 rounded-lg bg-slate-800/50 border border-slate-700"
            >
              <div className="flex-shrink-0 text-slate-400">
                <Lightbulb className="w-4 h-4" />
              </div>
              <p className="text-sm text-slate-400">{item}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default TabSection;
